import { useCallback, useEffect, useRef } from "react";
import {
    collectFrameGroups,
    mapGroupsWithoutFrameToFrames,
} from "@/appInterface/windows/ExportWindow/utils";
import type { EmojiCache } from "@/appInterface/windows/ExportWindow/VectorExport/emojiExportUtils";
import { canv } from "@/canv/global";
import { sortByIndexByParent } from "@/canvasController/helpers";
import type { frameWidget } from "@/elements/frame";
import { ImageWidget } from "@/elements/image";
import type { Group } from "@/group";
import { selectionManager } from "@/tools/cursorTool";
import PreviewWorker from "../preview.worker.ts?worker";

type FrameUID = string;

const waitForIdle = () =>
    new Promise<void>((resolve) => {
        if ("requestIdleCallback" in window) {
            requestIdleCallback(() => resolve());
        } else {
            setTimeout(resolve, 16);
        }
    });

export const useFramePreviews = (frames: frameWidget[]) => {
    const resultRef = useRef<Record<FrameUID, string>>({});
    const requestIdRef = useRef(0);
    const workerRef = useRef<Worker>();

    if (!workerRef.current) {
        workerRef.current = new PreviewWorker();
    }

    const getBlobFromWorker = useCallback((bitmap: ImageBitmap, width: number, height: number) => {
        const worker = workerRef.current!;

        return new Promise<Blob>((resolve) => {
            const handler = (e: MessageEvent) => {
                resolve(e.data.blob);
                worker.removeEventListener("message", handler);
            };

            worker.addEventListener("message", handler);

            worker.postMessage({ bitmap, width, height }, [bitmap]);
        });
    }, []);

    useEffect(() => {
        const worker = workerRef.current;

        return () => {
            worker?.terminate?.();

            if (resultRef.current) {
                Object.values(resultRef.current).forEach((url) => {
                    URL.revokeObjectURL(url);
                });
                resultRef.current = {};
            }
        };
    }, []);

    return useCallback(async (): Promise<Record<FrameUID, string>> => {
        const requestId = ++requestIdRef.current;
        // очищаем предыдущие URL сразу
        if (resultRef.current) {
            Object.values(resultRef.current).forEach(URL.revokeObjectURL);
            resultRef.current = {};
        }

        const prev = canv.multiManager.disableMultiCursors;
        canv.multiManager.disableMultiCursors = true;
        canv.setIsHighPdfExporting(true);

        const groups = frames.map((frame) => frame.parent);
        const { groupsWithFrame, groupsWithoutFrame } = collectFrameGroups(groups);

        const prefetchPromises: Promise<void>[] = [];
        groupsWithoutFrame.forEach((group) => {
            if (group.isItemWrapper() && group.children[0] instanceof ImageWidget) {
                prefetchPromises.push((group.children[0] as ImageWidget).prefetchHighLod());
            }
        });

        // если устарело — выходим
        if (requestId !== requestIdRef.current) return {};

        const selectedGroups = canv.canvasController.getSelected();

        const groupsByFrames = mapGroupsWithoutFrameToFrames(groupsWithFrame, groupsWithoutFrame);
        const savedTransformation = {
            0: canv.m[0],
            3: canv.m[3],
            4: canv.m[4],
            5: canv.m[5],
        };

        const appCtx = canv.ctx;

        const emojiCache: EmojiCache = new Map();
        const result: Record<FrameUID, string> = {};

        selectionManager.clearSelected();

        try {
            await Promise.all(prefetchPromises);

            const newCanvas = document.createElement("canvas");
            const newCtx = newCanvas.getContext("2d");

            if (!newCtx) {
                throw new Error("Не удалось получить 2d контекст");
            }

            const sortedGroupsByFrames: Record<string, Group[]> = {};

            for (const key in groupsByFrames) {
                const arr = [...groupsByFrames[key]];
                sortByIndexByParent(arr);
                sortedGroupsByFrames[key] = arr;
            }

            for (let i = 0; i < groupsWithFrame.length; i++) {
                const frameGroup = groupsWithFrame[i];

                if (i % 2 === 0) {
                    await waitForIdle();
                }
                // проверка на устаревание внутри цикла
                if (requestId !== requestIdRef.current) {
                    Object.values(result).forEach(URL.revokeObjectURL);
                    return {};
                }

                canv.zoomToSelected([frameGroup], true);

                const visualWidth = (frameGroup.width / canv.dpr) * canv.m[0];
                const visualHeight = (frameGroup.height / canv.dpr) * canv.m[0];

                const targetWidth = visualWidth * canv.dpr;
                const targetHeight = visualHeight * canv.dpr;

                if (newCanvas.width !== targetWidth) newCanvas.width = targetWidth;
                if (newCanvas.height !== targetHeight) newCanvas.height = targetHeight;

                canv.ctx = newCtx;

                canv.ctx.setTransform(
                    canv.m[0],
                    0,
                    0,
                    canv.m[3],
                    -frameGroup.startX * canv.m[0],
                    -frameGroup.startY * canv.m[0],
                );

                const groupsOnFrame = sortedGroupsByFrames[frameGroup.UID] || [];
                const groupsForPreview = [frameGroup, ...groupsOnFrame];

                canv.redraw();
                canv.redraw(true, true, groupsForPreview);

                const bitmap = await createImageBitmap(newCanvas);

                // проверка на отмену ДО тяжёлой операции
                if (requestId !== requestIdRef.current) {
                    bitmap.close();
                    Object.values(result).forEach(URL.revokeObjectURL);
                    return {};
                }

                const blob = await getBlobFromWorker(bitmap, newCanvas.width, newCanvas.height);

                // ещё одна защита
                if (requestId !== requestIdRef.current) {
                    Object.values(result).forEach(URL.revokeObjectURL);
                    return {};
                }

                if (!blob) continue;

                result[frameGroup.children[0].UID] = URL.createObjectURL(blob);

                canv.ctx.setTransform(1, 0, 0, 1, 0, 0);
                canv.ctx.clearRect(0, 0, newCanvas.width, newCanvas.height);
            }
        } catch (er) {
            console.log(er);
        } finally {
            emojiCache.clear();

            canv.ctx = appCtx;

            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation[0];
            canv.m[3] = savedTransformation[3];
            canv.m[4] = savedTransformation[4];
            canv.m[5] = savedTransformation[5];

            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
            canv.setIsHighPdfExporting(false);
            canv.multiManager.disableMultiCursors = prev;
        }

        // финальная проверка перед сохранением
        if (requestId !== requestIdRef.current) {
            Object.values(result).forEach(URL.revokeObjectURL);
            return {};
        }
        resultRef.current = result;
        return result;
    }, [frames, getBlobFromWorker]);
};
